import { useCallback, useEffect, MouseEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { GlobalState } from 'ha/types/store';
import { useLocation } from 'ha/helpers/router';
import { getIsAuthenticated } from 'ha/modules/AuthLogic/selectors';

import {
  addFavoriteListing,
  removeFavoriteListing,
  syncPendingFavoriteListing,
} from '../actions';

import {
  isLoading as isLoadingSelector,
  isFavorite as isFavoriteSelector,
} from '../selectors';

const useFavoriteListing = (listingId: number) => {
  const isLoadingSelectorWithId = useCallback(
    (state: GlobalState) => isLoadingSelector(state, listingId),
    [listingId],
  );
  const isFavoriteSelectorWithId = useCallback(
    (state: GlobalState) => isFavoriteSelector(state, listingId),
    [listingId],
  );

  const isLoading = useSelector(isLoadingSelectorWithId);
  const isFavorite = useSelector(isFavoriteSelectorWithId);
  const isAuthenticated = useSelector(getIsAuthenticated);

  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    dispatch(syncPendingFavoriteListing());
  }, [isAuthenticated]);

  const onClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (isLoading) {
        return;
      }

      if (isFavorite) {
        dispatch(removeFavoriteListing(listingId));
      } else {
        dispatch(
          addFavoriteListing(
            listingId,
            `${location.pathname}${location.search}`,
          ),
        );
      }
    },
    [listingId, isLoading, isFavorite, location],
  );

  return { onClick, isLoading, isFavorite };
};

export default useFavoriteListing;
